import { propsReadApi } from '../api/curator';
import { addIdsToRequestData } from '../components/admin/curator/CuratorHeader/useSaveProject';
import { GetRoomInfo, GetGroupPropsInfo } from '../components/container/unityContainer';
import { urlToBlob } from '../components/RetailAutomation/common/moduleHelper';
import { CAMERA_DEFAULT_FIELDS, TEMPORARY_BUILD_PROP_ID } from '../constants/constants';
import { CURRENT_USER, GUEST_USER } from '../constants/projectConstants';
import { FREEMIUM_TYPE, useUserContext } from '../contexts/UserContext';
import {
  getModelTemplateData,
  sendRenderToApi,
  sendRenderToApi360Model,
  apiEditTexture,
  apiCreateRender,
} from './api';
import { fetchAllPages } from './apiHelper';
import { getJson } from './axiosHelper';
import { getExtensionFromURL, getLocalStorage } from './jsHelper';
import { UnityEvent, waitForEventOnce } from './unityPubSub';

export const getScaleValue = (data) => {
  let flag = false;
  if (parseInt(data.scale_x) != 1 || parseInt(data.scale_y) != 1 || parseInt(data.scale_z) != 1) {
    flag = true;
  }
  return flag;
};

export const getSceneJson = async () => {
  return await waitForEventOnce(UnityEvent.GetRoomInfo, () => GetRoomInfo('normal'));
};

const createTransformData = (templateData, uploadedProductId) => {
  const baseTransformData = {
    objectName: `1@${uploadedProductId}`,
    xPosition: templateData?.position_x,
    yPosition: templateData?.position_y,
    zPosition: templateData?.position_z,
    xRotation: templateData?.rotation_x,
    yRotation: templateData?.rotation_y,
    zRotation: templateData?.rotation_z,
    wRotation: templateData?.rotation_w,
    xScale: templateData?.scale_x,
    yScale: templateData?.scale_y,
    zScale: templateData?.scale_z,
    isScaled: getScaleValue(templateData),
    centerPivotPositionX: 0,
    centerPivotPositionY: 0,
    centerPivotPositionZ: 0,
  };
  return { [`1@${uploadedProductId}`]: baseTransformData };
};

function blobToJson(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Event listener for successful read
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result); // Parse the JSON
        resolve(json); // Resolve the parsed JSON
      } catch (error) {
        reject(new Error('Invalid JSON format')); // Handle parsing error
      }
    };

    // Event listener for errors
    reader.onerror = () => {
      reject(new Error('Failed to read the Blob'));
    };

    // Read the blob as text
    reader.readAsText(blob);
  });
}

const createJsonForPropGroup = async ({ copyJson = {}, data = {}, templateData = {} }) => {
  let _copyJson = { ...copyJson };
  if (data?.is_prop_group && data?.product_file) {
    try {
      let productFile = data?.product_file;
      let productJsonData = null;
      const isJson = productFile?.includes('.json');
      if (isJson) {
        productJsonData = await getJson(productFile);
      } else {
        // converting blob to json text
        productJsonData = await fetch(productFile).then((r) => r.blob());
        productJsonData = await blobToJson(productJsonData);
      }

      _copyJson = {
        ...productJsonData,
        is_prop_group_render: true,
        model_rendering_template_id: templateData?.id,
      };

      // Add group_prop_child_objects
      _copyJson['group_prop_child_objects'] = _copyJson?.blenderAssetBundleDatas
        ?.map((x) => x.objectName)
        .filter(Boolean);

      // Add the transformChangedObjectsData for this GroupProp to the existing data.
      const baseTransformData = {
        objectName: `1@${data?.id}`,
        xPosition: templateData?.position_x,
        yPosition: templateData?.position_y,
        zPosition: templateData?.position_z,
        xRotation: templateData?.rotation_x,
        yRotation: templateData?.rotation_y,
        zRotation: templateData?.rotation_z,
        wRotation: templateData?.rotation_w,
        xScale: templateData?.scale_x,
        yScale: templateData?.scale_y,
        zScale: templateData?.scale_z,
        isScaled: getScaleValue(templateData),
        centerPivotPositionX: 0,
        centerPivotPositionY: 0,
        centerPivotPositionZ: 0,
      };
      _copyJson['transformChangedObjectData'][`1@${data?.id}`] = baseTransformData;

      // console.log('DEBUG___productJsonData', { productJsonData, copyJson, isJson });
      return _copyJson;
    } catch (e) {
      return _copyJson;
    }
  }
  return _copyJson;
};

export const sendRenderForAutomation = async ({
  uploadedProductId,
  uploadedTextureId = null,
  roomJSON,
  data = null,
  onlyDimensionRender = false,
  excludeDimensionRender = false,
} = {}) => {
  const _data =
    !data && uploadedProductId ? (await propsReadApi([uploadedProductId]))?.results?.[0] : data;

  // console.log('DEBUG___sendRenderForAutomation', {
  //   _data,
  //   data,
  //   uploadedProductId,
  // });

  let isProductBuilder = false;
  if (!uploadedTextureId && _data) {
    if (_data?.is_prop_group) {
      try {
        let productFile = data?.product_file;
        let productJsonData = null;
        const isJson = productFile?.includes('.json');
        if (isJson) {
          productJsonData = await getJson(productFile);
        } else {
          // converting blob to json text
          productJsonData = await fetch(productFile).then((r) => r.blob());
          productJsonData = await blobToJson(productJsonData);
        }
        if (productJsonData?.isProductBuilder) {
          isProductBuilder = true;
        }
      } catch (e) {
        console.log('Exception occured while reading prop group json: ${e}');
      }
    }
  }

  const userData = JSON.parse(getLocalStorage(CURRENT_USER));
  const isGuestUser = JSON.parse(getLocalStorage(GUEST_USER)) || false;
  const isFreemium =
    userData?.organization_detail?.subscription_type?.toLowerCase() === FREEMIUM_TYPE.toLowerCase();

  const query = uploadedTextureId
    ? `&is_automated_template=true&is_texture_template=false&product_texture_template=true&is_360_template=false`
    : `product_model_template=true&is_automated_template=true&is_360_template=false&is_product_builder_template=${isProductBuilder}`;

  let modelTemplateData = (
    await fetchAllPages(
      async (page) => await getModelTemplateData({ query: `${query}&page=${page}` })
    )
  )?.results
    ?.map((a) => {
      if (uploadedTextureId && a?.room_type === '3' && !a?.product) {
        return { ...a, isSiloRoomTextureRender: true };
      }
      return uploadedProductId && uploadedTextureId
        ? a?.product === uploadedProductId
          ? a
          : null
        : a;
    })
    .filter(Boolean);

  if (onlyDimensionRender) {
    modelTemplateData = modelTemplateData?.filter((a) => a.is_dimension_shot);
  }

  if (excludeDimensionRender) {
    modelTemplateData = modelTemplateData?.filter((a) => !a.is_dimension_shot);
  }

  // Check if there are entries with room_type '3' and no product
  if (uploadedTextureId) {
    const siloRoomEntries = modelTemplateData.filter((a) => a?.room_type === '3');
    const hasNoProduct = siloRoomEntries.some((a) => !a?.product);

    if (siloRoomEntries.length > 1 && hasNoProduct) {
      modelTemplateData = modelTemplateData.filter((a) => {
        // Remove the entries where isSiloRoomTextureRender was set
        return !(a?.room_type === '3' && !a?.product && a?.isSiloRoomTextureRender);
      });
    }
  }

  const resultJson = [];

  await Promise.allSettled(
    modelTemplateData?.map(async (templateData) => {
      if (!templateData?.id) return;

      const templateJson = await getJson(templateData?.template_json);
      let copyJson = {
        ...(uploadedTextureId ? await getSceneJson() : {}),
        materialDetails: [],
        model_rendering_template_id: templateData?.id,
      };

      const formObj = new FormData();
      const renderParams = {
        is_preview: 'false',
        is_main_render: 'true',
        render_name: templateData?.template_details?.name || `Render_${templateData.id}`,
        count: '1',
        is_meta_data: 'false',
        is_water_mark: isFreemium ? 'true' : 'false',
        is_transparent_bg: 'false',
        is_shadow_on_floor: 'true',
        render_extension: 'jpg',
        type: templateData?.room_type,
        room: templateData?.template,
        model_template: templateData?.id,
        is_guest_mode: isGuestUser,
      };

      if (onlyDimensionRender) {
        renderParams['render_name'] = (_data?.name + '_Dimension_Shot') || renderParams.render_name
      }

      if (templateData?.is_dimension_shot) {
        formObj.append('is_dimension_shot', true);
      }

      Object.entries(renderParams).forEach(([key, value]) => formObj.append(key, value));

      if (uploadedTextureId) {
        formObj.append('textures', uploadedTextureId);
        formObj.append('product_texture_render', true);
        const sceneJson = roomJSON || (await getSceneJson());
        sceneJson?.materialDetails?.forEach((data) => {
          data['textureObjectName'] = templateData?.product_name ?? `1@${uploadedProductId}`;
        });
        copyJson['productTextureMaterialsDetails'] = sceneJson['materialDetails'];
        copyJson['blenderAssetBundleDatas'] = [];
        if (templateData?.isSiloRoomTextureRender) {
          // formObj.append('products', uploadedProductId);
          copyJson['uploaded_model_id'] = uploadedProductId;
          copyJson['product_texture_silo_render'] = true;
          copyJson['blenderAssetBundleDatas'] = [sceneJson['blenderAssetBundleDatas']?.[0]];
          copyJson['transformChangedObjectData'] = createTransformData(
            templateData,
            uploadedProductId
          );
        }
      } else {
        // Removing .skp file support
        // if (getExtensionFromURL(_data?.prop_data_user_file) === '.skp') {
        //   // Skp File render
        //   formObj.append('is_skp', true);
        // }
        copyJson['blenderAssetBundleDatas'] = [
          {
            assetBundleName: null,
            objectName: `1@${uploadedProductId}`,
            blenderAssetbundleId: uploadedProductId,
            parentName: null,
            id: uploadedProductId,
            objectIndex: '1',
            parentMat: null,
            childrenObjects: [],
            childrenMaterials: [],
            isDuplicateProp: false,
            isWindowAndDoor: false,
            loadedPropType: 'None',
            modelType: 'UserImportModel',
          },
        ];
        copyJson['transformChangedObjectData'] = createTransformData(
          templateData,
          uploadedProductId
        );

        copyJson = await createJsonForPropGroup({
          copyJson,
          data: _data,
          templateData,
        });

        if (_data?.is_prop_group) {
          formObj.append('products', uploadedProductId);
        }

        formObj.append('product_model_render', true);
        if (templateData?.is_dimension_shot) {
          copyJson['is_dimension_shot'] = templateData?.is_dimension_shot;
        }
        copyJson['uploaded_model_id'] = uploadedProductId;
        if (isProductBuilder) {
          copyJson['is_product_builder_template'] = true;
        }
      }

      copyJson['cameraList'] = templateData?.camera_angle_details
        ? [templateData?.camera_angle_details]
        : [];
      copyJson['roomId'] = templateData?.template;
      copyJson['baseRoomId'] = +templateJson?.blenderLayoutBundleDatas?.[0]?.id;

      if (!_data?.is_prop_group) {
        addIdsToRequestData({
          data: formObj,
          roomInfo: copyJson,
          includedIds: { products: true, textures: true },
          keepUniqueIds: { products: true },
        });
      }

      // console.log('DEBUG___Render_Data', { copyJson, templateData, templateJson });

      const blob = new Blob([JSON.stringify(copyJson)], { type: 'application/json' });
      formObj.append('json_file', blob, 'room_info.json');
      formObj.append('is_main_render', 'false');

      try {
        const renderData = await sendRenderToApi(formObj);
        resultJson.push({
          renderId: renderData?.data?.id,
          type: templateData?.room_type,
          renderUploadId: renderData?.data?.render_upload_id,
          cameraData: renderData?.data?.CameraData?.[0],
        });
      } catch (error) {
        console.error('Error sending render data:', error);
      }
    })
  );

  return true;
};

export const sendRenderForAutomationManualSelectionTemplates = async ({
  uploadedProductId,
  uploadedTextureId = null,
  roomJSON,
  excludeDimensionRender = false,
  data = null,
  selectedListRoomList = [],
} = {}) => {
  const _data =
    !data && uploadedProductId ? (await propsReadApi([uploadedProductId]))?.results?.[0] : data;

  if (!uploadedTextureId && _data) {
    if (_data?.is_prop_group) {
      try {
        let productFile = data?.product_file;
        let productJsonData = null;
        const isJson = productFile?.includes('.json');
        if (isJson) {
          productJsonData = await getJson(productFile);
        } else {
          // converting blob to json text
          productJsonData = await fetch(productFile).then((r) => r.blob());
          productJsonData = await blobToJson(productJsonData);
        }
      } catch (e) {
        console.log('Exception occured while reading prop group json: ${e}');
      }
    }
  }

  const userData = JSON.parse(getLocalStorage(CURRENT_USER));
  const isGuestUser = JSON.parse(getLocalStorage(GUEST_USER)) || false;
  const isFreemium =
    userData?.organization_detail?.subscription_type?.toLowerCase() === FREEMIUM_TYPE.toLowerCase();
  await Promise.allSettled(
    selectedListRoomList?.map(async (element) => {
      const query = `&is_pdp_v2=true&template=${element?.id}`;
      let modelTemplateData = (
        await fetchAllPages(
          async (page) => await getModelTemplateData({ query: `${query}&page=${page}` })
        )
      )?.results?.filter(Boolean);
      if (!modelTemplateData) {
        console.log('modelTemplateData RRR', modelTemplateData);
        return;
      }

      if (excludeDimensionRender) {
        modelTemplateData = modelTemplateData?.filter((a) => !a.is_dimension_shot);
      }

      const resultJson = [];
      await Promise.allSettled(
        modelTemplateData?.map(async (templateData) => {
          try {
            if (!templateData?.id) return;
            if (!templateData?.template_json) {
              return;
            }
            const templateJson = await getJson(templateData?.template_json);
            let copyJson = {
              materialDetails: [],
              model_rendering_template_id: templateData?.id,
              is_pdp_v2: true,
              product_to_replace: templateData?.product_name
            };
            const formObj = new FormData();
            const renderParams = {
              is_preview: 'false',
              is_main_render: 'true',
              render_name: data?.name || `Render_${templateData.id}`,
              count: '1',
              is_meta_data: 'false',
              is_water_mark: isFreemium ? 'true' : 'false',
              is_transparent_bg: 'false',
              is_shadow_on_floor: 'true',
              render_extension: 'jpg',
              type: templateData?.room_type,
              room: templateData?.template,
              model_template: templateData?.id,
              is_guest_mode: isGuestUser,
            };
            Object.entries(renderParams).forEach(([key, value]) => formObj.append(key, value));
            // Removing .skp file support
            // if (getExtensionFromURL(_data?.prop_data_user_file) === '.skp') {
            //   // Skp File render
            //   formObj.append('is_skp', true);
            // }
            copyJson['blenderAssetBundleDatas'] = [
              {
                assetBundleName: null,
                objectName: `1@${uploadedProductId}`,
                blenderAssetbundleId: uploadedProductId,
                parentName: null,
                id: uploadedProductId,
                objectIndex: '1',
                parentMat: null,
                childrenObjects: [],
                childrenMaterials: [],
                isDuplicateProp: false,
                isWindowAndDoor: false,
                loadedPropType: 'None',
                modelType: 'UserImportModel',
              },
            ];
            copyJson['transformChangedObjectData'] = createTransformData(
              templateData,
              uploadedProductId
            );
            copyJson = await createJsonForPropGroup({
              copyJson,
              data: _data,
              templateData,
            });

            if (_data?.is_prop_group) {
              formObj.append('products', uploadedProductId);
              copyJson['is_pdp_v2'] = true;  
              copyJson['product_to_replace'] = templateData?.product_name;
            }
            formObj.append('product_model_render', true);
            copyJson['uploaded_model_id'] = uploadedProductId;
            copyJson['roomId'] = templateData?.template;
            copyJson['baseRoomId'] = +templateJson?.blenderLayoutBundleDatas?.[0]?.id;

            if (!_data?.is_prop_group) {
              addIdsToRequestData({
                data: formObj,
                roomInfo: copyJson,
                includedIds: { products: true, textures: true },
                keepUniqueIds: { products: true },
              });
            }
            await Promise.allSettled(
              templateData?.room_camera_angles?.map(async (roomAngle) => {
                const formObjClone = new FormData();
                // Copy all entries from formObj to formObjClone
                for (let [key, value] of formObj.entries()) {
                  formObjClone.append(key, value);
                }
                
                copyJson['cameraList'] = [{...roomAngle}];
                const blob = new Blob([JSON.stringify(copyJson)], { type: 'application/json' });
                formObjClone.append('json_file', blob, 'room_info.json');
                formObjClone.append('room_cam_angle_id', roomAngle?.id);
                try {
                  const renderData = await sendRenderToApi(formObjClone);
                  resultJson.push({
                    renderId: renderData?.data?.id,
                    type: templateData?.room_type,
                    renderUploadId: renderData?.data?.render_upload_id,
                    cameraData: renderData?.data?.CameraData?.[0],
                  });
                } catch (error) {
                  console.error('Error sending render data:', error);
                }
              })
            );
          } catch (error) {
            console.error('Error processing templateData:', templateData?.id, error);
          }
        })
      );
    })
  );
  return true;
};

export const sendBuilderRenderForAutomation = async ({
  uploadedProductId,
  data,
  modelTemplateData,
  projectId,
} = {}) => {
  const userData = JSON.parse(getLocalStorage(CURRENT_USER));
  const isGuestUser = JSON.parse(getLocalStorage(GUEST_USER)) || false;
  const isFreemium = userData?.organization_detail?.subscription_type?.toLowerCase() === FREEMIUM_TYPE.toLowerCase();

  const response = await Promise.allSettled(
    modelTemplateData?.map(async (templateData) => {
      if (!templateData?.id) return;

      const templateJson =
        templateData?.template_json_data || (await getJson(templateData?.template_json));
      let copyJson = {
        materialDetails: [],
        model_rendering_template_id: templateData?.id,
      };

      const formObj = new FormData();
      const renderParams = {
        is_preview: 'false',
        is_main_render: 'true',
        render_name: templateData?.template_details?.name || `Render_${templateData.id}`,
        count: '1',
        is_meta_data: 'false',
        is_water_mark: isFreemium ? 'true' : 'false',
        is_transparent_bg: 'false',
        is_shadow_on_floor: 'true',
        render_extension: 'jpg',
        type: templateData?.room_type,
        room: templateData?.template,
        model_template: templateData?.id,
        is_guest_mode: isGuestUser,
        is_product_builder: true,
        save_mattress_template_type: true, // here, we are saving the mattress template type so that we can open the mattress or render project based on it when we do Edit in 3D on render images.
      };

      Object.entries(renderParams).forEach(([key, value]) => formObj.append(key, value));

      copyJson['blenderAssetBundleDatas'] = [
        {
          assetBundleName: null,
          objectName: `1@${uploadedProductId}`,
          blenderAssetbundleId: uploadedProductId,
          parentName: null,
          id: uploadedProductId,
          objectIndex: '1',
          parentMat: null,
          childrenObjects: [],
          childrenMaterials: [],
          isDuplicateProp: false,
          isWindowAndDoor: false,
          loadedPropType: 'None',
          modelType: 'UserImportModel',
        },
      ];

      copyJson['transformChangedObjectData'] = createTransformData(
        templateData,
        uploadedProductId
      );

      copyJson = await createJsonForPropGroup({
        copyJson,
        data: data,
        templateData,
      });

      formObj.append('products', uploadedProductId);
      formObj.append('product_model_render', true);

      copyJson['uploaded_model_id'] = uploadedProductId;
      copyJson['is_product_builder_template'] = true;

      // Filter unwanted keys from camera JSON
      const cameras = templateData?.camera_angle_details?.map(cam => {
        const camData = {}
        Object.keys(cam)?.forEach(key => {
          if (CAMERA_DEFAULT_FIELDS?.includes(key)) {
            camData[key] = cam[key]
          }
        })
        return camData
      })

      copyJson['cameraInfo'] = cameras
      copyJson['roomId'] = templateData?.template;
      copyJson['baseRoomId'] = +templateJson?.blenderLayoutBundleDatas?.[0]?.id;

      const blob = new Blob([JSON.stringify(copyJson)], { type: 'application/json' });
      formObj.append('json_file', blob, 'room_info.json');

      await Promise.all(
        templateData?.camera_angle_details?.map(async (cam) => {
          let blobFileWithName = cam?.blobFileWithName;

          if (!blobFileWithName) {
            // fetching the camera thumbnail if not prefetched
            const fileName = cam?.cameraName ? `${cam?.cameraName}.jpg` : 'angle_image.jpg'; // Fallback name if cameraName is not available
            const blobFile = cam?.thumbnail ? await urlToBlob(cam?.thumbnail) : null;
            blobFileWithName = blobFile
              ? new File([blobFile], fileName, { type: 'image/jpeg' })
              : null;
          }

          formObj.append('camera_angle_name', cam?.cameraName);
          formObj.append('camera_angle_file', blobFileWithName);
          formObj.append('camera_angle_file_reso_x', cam?.resoX);
          formObj.append('camera_angle_file_reso_y', cam?.resoY);
        })
      );

      formObj.append('count', templateData?.camera_angle_details?.length);
      formObj.append('project', projectId);
      // console.log("DEBUG__Render Data", copyJson, formObj);
      try {
        const response = await apiCreateRender(formObj);
        return response?.data || null;
      } catch (error) {
        console.error('Error sending render data:', error);
      }
    })
  );

  return response;
};

const createCopyJson = async ({
  uploadedTextureId,
  templateData,
  uploadedProductId,
  roomJSON,
  data = null,
}) => {
  let copyJson = {
    blenderAssetBundleDatas: [],
    transformChangedObjectData: {},
    materialDetails: [],
    model_rendering_template_id: templateData?.id,
  };

  if (uploadedTextureId) {
    const sceneJson = roomJSON || (await getSceneJson());
    sceneJson?.materialDetails?.forEach((data) => {
      data['textureObjectName'] = templateData?.product_name ?? `1@${uploadedProductId}`;
    });

    copyJson['is_product_page_360_texture_render'] = true;
    copyJson['productTextureMaterialsDetails'] = sceneJson['materialDetails'];

    if (templateData?.isSiloRoomTextureRender) {
      copyJson['uploaded_model_id'] = uploadedProductId;
      copyJson['product_texture_silo_render'] = true;
      copyJson['blenderAssetBundleDatas'].push(sceneJson['blenderAssetBundleDatas']?.[0]);
      copyJson['transformChangedObjectData'] = createTransformData(templateData, uploadedProductId);
    }
  } else {
    copyJson['blenderAssetBundleDatas'].push(createBlenderAssetData(uploadedProductId));
    copyJson['transformChangedObjectData'] = createTransformData(templateData, uploadedProductId);
    copyJson = await createJsonForPropGroup({
      copyJson,
      data,
      templateData,
    });

    copyJson['uploaded_model_id'] = uploadedProductId;
  }

  // console.log('DEBUG___Render_Data_360', { copyJson, templateData });
  return copyJson;
};

const createBlenderAssetData = (uploadedProductId) => {
  return {
    assetBundleName: null,
    objectName: '1@' + uploadedProductId,
    blenderAssetbundleId: uploadedProductId,
    parentName: null,
    id: uploadedProductId,
    objectIndex: '1',
    parentMat: null,
    childrenObjects: [],
    childrenMaterials: [],
    isDuplicateProp: false,
    isWindowAndDoor: false,
    loadedPropType: 'None',
    modelType: 'UserImportModel',
  };
};

const createFormData = (templateData, uploadedProductId, uploadedTextureId, copyJson) => {
  const formObj = new FormData();
  formObj.append('color', templateData?.color);

  if (uploadedTextureId) {
    formObj.append('texture', uploadedTextureId);
  }

  const blob = new Blob([JSON.stringify(copyJson)], { type: 'application/json' });
  formObj.append('json_file', blob, 'room_info.json');
  formObj.append('automated_product_render', 'true');
  formObj.append('height', 1500);
  formObj.append('width', 1500);
  formObj.append('product', uploadedProductId);
  formObj.append('degree', 85);
  formObj.append('rooms_list', JSON.stringify(templateData?.template));

  return formObj;
};

export const sendRenderForAutomation360 = async ({
  uploadedProductId,
  uploadedTextureId,
  roomJSON,
  data,
}) => {
  const _data =
    !data && uploadedProductId ? (await propsReadApi([uploadedProductId]))?.results?.[0] : data;

  const isTextureUpload = Boolean(uploadedTextureId);
  const isGuestUser = JSON.parse(getLocalStorage(GUEST_USER)) || false;
  // Construct the query based on whether a texture ID is provided
  const query = isTextureUpload
    ? '&is_automated_template=true&is_texture_template=false&product_texture_template=true&is_360_template=true'
    : 'product_model_template=true&is_automated_template=true&is_360_template=true';

  const modelTemplateData = (
    await fetchAllPages(
      async (page) => await getModelTemplateData({ query: `${query}&page=${page}` })
    )
  )?.results
    ?.map((a) => {
      let isSiloRoomTextureRender = false;

      if (isTextureUpload) {
        if (a?.room_type === '3' && !a?.product) {
          isSiloRoomTextureRender = true;
        }
        if (!isSiloRoomTextureRender && a?.product !== uploadedProductId) {
          return null; // Return null instead of false for filtering
        }
      }
      return { ...a, isSiloRoomTextureRender };
    })
    .filter(Boolean);

  await Promise.allSettled(
    modelTemplateData?.map(async (templateData) => {
      if (!templateData?.id) return;

      const copyJson = await createCopyJson({
        uploadedTextureId,
        templateData,
        uploadedProductId,
        roomJSON,
        data: _data,
      });
      const formObj = createFormData(templateData, uploadedProductId, uploadedTextureId, copyJson);
      formObj.append('is_guest_mode', isGuestUser);

      // Removing .skp file support
      // if (!uploadedTextureId && _data) {
      //   if (getExtensionFromURL(_data?.prop_data_user_file) === '.skp') {
      //     // Skp File render
      //     formObj.append('is_skp', true);
      //   }
      // }

      try {
        const renderData = await sendRenderToApi360Model(formObj, uploadedTextureId);
        console.log('Render successful:', renderData);
      } catch (error) {
        console.error('Error sending render data:', error);
      }
    })
  );

  return true;
};

function extractContentBetweenUnderscores(inputString) {
  if (!inputString) {
    return '';
  }

  const firstUnderscoreIndex = inputString.indexOf('_');
  const lastUnderscoreIndex = inputString.lastIndexOf('_');

  // Ensure underscores are found and are in the correct order
  if (firstUnderscoreIndex >= 0 && firstUnderscoreIndex < lastUnderscoreIndex) {
    return inputString.substring(firstUnderscoreIndex + 1, lastUnderscoreIndex);
  }

  return ''; // Return empty string if conditions are not met
}

function getRealName(name) {
  return name
    .split("_") // Split the name by underscores
    .filter((x) => !x.includes("MeshPart") && !x.includes("dupli")) // Exclude parts containing "MeshPart" or "dupli"
    .join("_"); // Join the remaining parts with underscores
}

export const updateTextureDataForTexturePage = async (
  uploadedProductId,
  uploadedTextureId,
  roomJSON
) => {
  const isGuestUser = JSON.parse(getLocalStorage(GUEST_USER)) || false;
  // console.log('DEBUG___updateTextureDataForTexturePage', {
  //   uploadedProductId,
  //   uploadedTextureId,
  //   roomJSON,
  // });

  // For this texture, update the linked product & also
  // inform the parts where we need to apply the texture on the glb file.
  const data = { texture_page_product: uploadedProductId };
  const sceneJson = roomJSON || (await getSceneJson());

  // Get all objectName from all material Details
  const objectNames = sceneJson?.materialDetails?.map((data) => data.objectName).filter(Boolean);
  // Extract and modify object names
  const modifiedNames = objectNames
    .map((name) => {
      const extractedName = getRealName(extractContentBetweenUnderscores(name));
      return extractedName ? extractedName : null;
    })
    .filter((name) => name !== null); // Filter out null values

  // Create a comma-separated list
  data['texture_page_changed_product_parts'] = modifiedNames.join(',');

  return await apiEditTexture(uploadedTextureId, data, `is_guest_user=${isGuestUser}`, null);
};